<template>
  <div class="mb-8 flex flex-wrap px-2 text-sm text-slate-900" :class="small ? 'gap-y-4' : 'gap-y-6'">
    <div v-for="(field, index) in basicInfoFields" :key="index" class="flex w-full items-center justify-start gap-3"
         :class="small ? 'w-full' : 'sm:w-1/2'">
      <Component v-if="field.iconType === 'icon'" :is="field.icon" :class="field.iconClasses"/>
      <img v-else-if="field.iconType === 'img'" :src="field.icon" :alt="field.label" :class="field.iconClasses"/>
      <p v-if="isEditingIndex !== index || field.readonly" @click="setIsEditing(index, field?.readonly)"
         :class="editable && !field.readonly && 'hover:underline group/candidate-item cursor-pointer'">
        {{ field.label }}
        <b>{{ field.text }}</b>
        <PencilSquareIcon v-if="editable && !field.readonly && isEditingIndex !== index"
                          class="inline-block h-4 w-4 text-slate-600 opacity-50 group-hover/candidate-item:opacity-100 transition-all ml-1.5"/>
      </p>
      <div v-if="isEditingIndex === index && !field.readonly" class="w-full md:pr-8 lg:pr-12">
        <Component :is="answerComponent" v-bind="selectedInfoField.props" v-on="selectedInfoField.eventHandlers"/>
        <FormButton color="green" @click="setIsEditing(-1)" class="mt-2 mr-2">
          Done
        </FormButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import FormButton from "./FormButton.vue";
import FormCheckboxes from "./FormCheckboxes.vue";
import FormCheckboxesSearch from "./FormCheckboxesSearch.vue";
import FormSelectDropdown from "./FormSelectDropdown.vue";
import FormSliderPayAndHourly from "./FormSliderPayAndHourly.vue";
import {arrayReadable, availabilityReadable} from "../helpers/CandidateFunctions.js";
import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ClockIcon,
  CurrencyDollarIcon,
  LanguageIcon,
  PaperAirplaneIcon,
} from "@heroicons/vue/24/outline/index.js";
import {computed, ref} from "vue";
import {PencilSquareIcon} from "@heroicons/vue/24/solid/index.js";
import {TYPE_JOB_TITLE} from "../constants/SupportRequests.js";
import {employmentTypes, industries, jobs, languages, weekdays} from "../constants/Lists.js";

const emits = defineEmits(["update:candidate"]);
const props = defineProps({
  candidate: Object,
  small: Boolean,
  editable: Boolean,
});

const isEditingIndex = ref(-1);

const setIsEditing = (index = -1, readonly = false) => {
  if (!props.editable || readonly) return;
  isEditingIndex.value = index;
};

const updateCandidate = (field, value) => {
  const updatedCandidate = {...props.candidate};
  updatedCandidate[field] = value;
  emits("update:candidate", updatedCandidate);
};

const getCompensation = (prefersHourly, desiredHourly, flexibility) => {
  if (!props.editable) {
    return prefersHourly ? "Prefers Hourly" : "Prefers Salary";
  }

  const roundToNearestThousand = value => {
    if (value < 1000) return value;
    return Math.round(value / 1000) * 1000;
  };

  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(roundToNearestThousand(desiredHourly * (prefersHourly ? 1 : 2080)));

  const unit = prefersHourly ? "/hr" : "/yr";

  const flexibilityBuckets = [
    {label: "Not Flexible ❌", min: 0, max: 0.12},
    {label: "A Little Flexible", min: 0.12, max: 0.18},
    {label: "Somewhat Flexible", min: 0.18, max: 0.26},
    {label: "Very Flexible", min: 0.26, max: 1},
  ];

  const flexibilityLabel = flexibilityBuckets.find(bucket => flexibility >= bucket.min && flexibility <= bucket.max)?.label;
  return `${formattedAmount}${unit} (${flexibilityLabel})`;

};

const basicInfoFields = computed(() => {
  return [
    {
      iconType: "img",
      icon: "https://resources.businessdraft.com/bd-coin.svg",
      iconClasses: "w-4",
      label: "Trust Score: ",
      text: `${props.candidate?.trustScore}%`,
      readonly: true,
    },
    {
      iconType: "icon",
      icon: BriefcaseIcon,
      iconClasses: "w-3.5 h-3.5 min-w-[0.75rem]",
      label: "Interested in: ",
      text: arrayReadable(props.candidate?.interestedIn),
      value: props.candidate?.interestedIn,
      component: FormCheckboxesSearch,
      props: {
        modelValue: props.candidate?.interestedIn,
        allowSupportRequest: true,
        supportRequestType: TYPE_JOB_TITLE,
        placeholder: "Search job titles",
        getOptions: (params) => jobs(params),
      },
      eventHandlers: {
        "update:modelValue": (value) => updateCandidate("interestedIn", value),
      },
    },
    {
      iconType: "icon",
      icon: PaperAirplaneIcon,
      iconClasses: "w-3.5 h-3.5 min-w-[0.75rem]",
      label: "Willing to Relocate: ",
      text: props.candidate?.willingToRelocate || "Not specified",
      value: props.candidate?.willingToRelocate,
      component: FormSelectDropdown,
      props: {
        modelValue: props.candidate?.willingToRelocate,
        options: ["Open to Relocate", "Might Relocate", "No Relocation"],
        name: "Willing to relocate",
      },
      eventHandlers: {
        "update:modelValue": (value) => updateCandidate("willingToRelocate", value),
      },
    },
    {
      iconType: "icon",
      icon: BuildingOffice2Icon,
      iconClasses: "w-3.5 h-3.5 min-w-[0.75rem]",
      label: "Industry: ",
      text: arrayReadable(props.candidate?.industries),
      value: props.candidate?.industries,
      component: FormCheckboxesSearch,
      props: {
        modelValue: props.candidate?.industries,
        placeholder: "Search industries",
        maxAllowed: 5,
        getOptions: (params) => industries(params),
      },
      eventHandlers: {
        "update:modelValue": (value) => updateCandidate("industries", value),
      },
    },
    {
      iconType: "icon",
      icon: ClockIcon,
      iconClasses: "w-3.5 h-3.5 min-w-[0.75rem]",
      label: "Seeking: ",
      text: `${arrayReadable(props.candidate?.fullTime)} work`,
      value: props.candidate?.fullTime,
      component: FormCheckboxes,
      props: {
        modelValue: props.candidate?.fullTime,
        options: employmentTypes,
        name: "employment-types",
      },
      eventHandlers: {
        "update:modelValue": (value) => updateCandidate("fullTime", value),
      },
    },
    {
      iconType: "icon",
      icon: CalendarDaysIcon,
      iconClasses: "w-3.5 h-3.5 min-w-[0.75rem]",
      label: "Available: ",
      text: availabilityReadable(props.candidate?.availability),
      value: props.candidate?.availability,
      component: FormCheckboxes,
      props: {
        modelValue: props.candidate?.availability,
        options: weekdays,
        name: "weekdays",
      },
      eventHandlers: {
        "update:modelValue": (value) => updateCandidate("availability", value),
      },
    },
    {
      iconType: "icon",
      icon: CurrencyDollarIcon,
      iconClasses: "w-3.5 h-3.5 min-w-[0.75rem]",
      label: props.editable ? "(Not Shared)" : "Compensation: ",
      text: getCompensation(props.candidate?.prefersHourlyOverSalary, props.candidate?.desiredHourlyPay, props.candidate?.compensationFlexibility),
      value: props.candidate?.prefersHourlyOverSalary,
      component: FormSliderPayAndHourly,
      props: {
        prefersHourlyOverSalary: props.candidate?.prefersHourlyOverSalary,
        desiredHourlyPay: props.candidate?.desiredHourlyPay,
        compensationFlexibility: props.candidate?.compensationFlexibility,
      },
      eventHandlers: {
        "update:prefersHourlyOverSalary": (value) => updateCandidate("prefersHourlyOverSalary", value),
        "update:desiredHourlyPay": (value) => updateCandidate("desiredHourlyPay", value),
        "update:compensationFlexibility": (value) => updateCandidate("compensationFlexibility", value),
      },
    },
    {
      iconType: "icon",
      icon: LanguageIcon,
      iconClasses: "w-3.5 h-3.5 min-w-[0.75rem]",
      label: "Language(s): ",
      text: arrayReadable(props.candidate?.languages),
      value: props.candidate?.languages,
      component: FormCheckboxesSearch,
      props: {
        modelValue: props.candidate?.languages,
        placeholder: "Search languages",
        getOptions: (params) => languages(params),
      },
      eventHandlers: {
        "update:modelValue": (value) => updateCandidate("languages", value),
      },
    },
  ];
});

const selectedInfoField = computed(() => {
  if (isEditingIndex.value === -1) return;
  return basicInfoFields.value[isEditingIndex.value];
});

const answerComponent = computed(() => {
  if (!selectedInfoField.value || selectedInfoField.value.readonly) return;
  return selectedInfoField.value.component;
});
</script>

<style scoped>
.list-badge li {
  padding-bottom: 0.5rem;
}

.list-badge li::before {
  content: attr(data-icon);
  font-size: 0.75rem;
  margin-right: 1rem;
  margin-left: -1.375rem;
}
</style>
